<template>
  <div class="cont">
    <div class="timeline-container" ref="timeline">
      <div class="error" v-if="error">Произошла ошибка при загрузке плейлиста</div>
      <div class="canvas-container" ref="canvasContainer">
        <canvas
          ref="timelineCanvas"
          class="timeline-canvas"
          :width="canvasWidth"
          :height="canvasHeight"
          :style="{ width: halfCanvasWidth, height: halfCanvasHeight }"
          @mousedown="onMouseDown"
          @touchstart="onTouchStart"
          @wheel="onWheel"
        ></canvas>
      </div>
      <div class="scrubber" v-if="segments.length" ref="scrubber"></div>
      <div class="tt" :style="tooltipStyle"> {{ currentSegmentName }}</div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { shallowRef, ref } from 'vue'
import { useDefaultStore } from '@/store'
export default {
  name: 'TimelineNew',
  props: {
    camId: String
  },
  emits: ['playlist-loaded', 'seek', 'loading', 'timeline-ready', 'seek-start', 'seek-end'],
  setup() {
    const store = useDefaultStore()
    const playlistText = shallowRef('')
    const segments = shallowRef([])
    const touchStartDistance = ref(0)
    return {
      playlistText,
      segments,
      touchStartDistance,
      store
    }
  },
  created() {},
  data() {
    return {
      canvasWidth: 200,
      canvasHeight: 60,
      halfCanvasWidth: '100px',
      halfCanvasHeight: '30px',
      dpi: 1,
      offsetX: 0,
      scale: 1,
      loading: false,
      mouseDown: false,
      segmentsByDate: {},
      dragStartX: 0,
      date: new Date(),
      currentSegmentName: '',
      mergedSegments: [],
      mergeThreshold: 11000, // 11 seconds in milliseconds
      minScale: 1,
      maxScale: 1,
      error: false,
      totalLength: 0,
      allowMouseEvents: false,
      animationFrameId: null,
      seekEvents: 0,
      TWENTY_FOUR_HOURS: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
      MINIMUM_VISIBLE_DURATION: 60000, // 1 minute in milliseconds
      SERVER_TZ_OFFSET_MINUTES: 180, // GMT+3
      NEAR_END_THRESHOLD_MS: 10 * 60 * 1000, // 10 minutes near the end triggers refresh
      REFRESH_COOLDOWN_MS: 15000,
      lastRefreshAt: 0,
      lastSeekDayKey: null,
      pendingReseekPayload: null,
      END_EPSILON_SECONDS: 0.05,
      dayIndex: {},
    }
  },
  watch: {
    loading() {
      // this.$emit('loading', this.loading)
    }
  },
  computed: {
    totalTime() {
      if (!this.segments.length) return 0
      const lastSegment = this.segments[this.segments.length - 1]
      return lastSegment.s + lastSegment.d - this.segments[0].s
    },
    visibleStartTime() {
      if (!this.segments.length) return 0
      return this.segments[0].s + this.offsetX * this.totalTime
    },
    visibleEndTime() {
      if (!this.segments.length) return 0
      return this.visibleStartTime + this.totalTime / this.scale
    },
    centerTime() {
      return this.visibleStartTime + (this.visibleEndTime - this.visibleStartTime) / 2
    },
    videoSrc() {
      const uuid = this.store.currentDivision.guid
      const url =
        window.location.protocol === 'http:'
          ? `http://${this.store.currentDivision.address}`
          : `https://${this.store.currentDivision.url}`
      return `${url}/${uuid}/archive/alldays/${this.camId}/720/index.m3u8`
    },
    visibleTimeRange() {
      return {
        start: this.visibleStartTime,
        end: this.visibleEndTime
      }
    },
    tooltipStyle() {
      return {
        left: '50%',
        transform: 'translateX(-50%)',
        display: this.currentSegmentName ? 'block' : 'none'
      }
    },
    canvasStyle() {
      if (this.loading) {
        return {
          background: 'linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%)',
          animation: '1.5s shine linear infinite'
        }
      }
      return {}
    }
  },
  async mounted() {
    this.dpi = window.devicePixelRatio;
    this.handleResize()
    this.setupResizeObserver()
    await this.getSegmentsData()
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
    document.removeEventListener('mousemove', this.onMouseDrag)
    document.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('touchend', this.onTouchEnd)
    window.removeEventListener('touchmove', this.onTouchMove)
  },
  methods: {
    handleResize() {
      if (!this.$refs.canvasContainer) return
      const { width } = this.$refs.canvasContainer.getBoundingClientRect()
      this.canvasWidth = width * this.dpi
      this.canvasHeight = 30 * this.dpi
      this.halfCanvasWidth = `${width}px`
      this.halfCanvasHeight = `${30}px`
      const canvas = this.$refs.timelineCanvas
      canvas.width = this.canvasWidth
      canvas.height = this.canvasHeight
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      this.scheduleDraw()
      this.updateCurrentSegmentName()
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize()
      })
      this.resizeObserver.observe(this.$refs.canvasContainer)
    },
    async getSegmentsData() {
      this.loading = true
      this.$emit('loading', true)
      try {
        const url =
          window.location.protocol === 'http:'
            ? `http://${this.store.currentDivision.address}`
            : `https://${this.store.currentDivision.url}`
        const request = await fetch(`${url}/api/segmentData/${this.camId}`)
        if (!request.ok) {
          throw new Error(`Failed to fetch segments: ${request.status}`)
        }
        const data = await request.json()
        // Normalize keys to YYYY_MM_D and ensure numeric segment fields
        const normalizedByDate = {}
        for (const [key, arr] of Object.entries(data || {})) {
          const normalizedKey = moment(key, ['YYYY_MM_D', 'YYYY_M_D']).format('YYYY_MM_D')
          const normalizedArr = (arr || [])
            .map((s) => ({ s: Number(s.s), d: Number(s.d) }))
            .filter((s) => Number.isFinite(s.s) && Number.isFinite(s.d) && s.d > 0)
            .sort((a, b) => a.s - b.s)
          if (normalizedArr.length) normalizedByDate[normalizedKey] = normalizedArr
        }
        this.applyUnitNormalization(normalizedByDate)
        this.segmentsByDate = normalizedByDate
        let segments = []
        for (let value of Object.values(this.segmentsByDate)) {
          value = value.sort((a, b) => a.s - b.s)
          segments = segments.concat(value)
        }
        // Ensure global chronological order across all days
        segments = segments.sort((a, b) => a.s - b.s)
        this.segments = segments
        this.mergeSegments()
        if (typeof this.buildDayIndex === 'function') {
          this.buildDayIndex()
        } else {
          const index = {}
          for (const [key, arr] of Object.entries(this.segmentsByDate)) {
            const starts = new Array(arr.length)
            const ends = new Array(arr.length)
            const prefix = new Array(arr.length + 1)
            prefix[0] = 0
            for (let i = 0; i < arr.length; i++) {
              starts[i] = arr[i].s
              ends[i] = arr[i].s + arr[i].d
              prefix[i + 1] = prefix[i] + arr[i].d
            }
            index[key] = { starts, ends, prefix, totalMs: prefix[prefix.length - 1] }
          }
          this.dayIndex = index
        }
        this.setLastDayVisible(false)
        const time = this.calculateSeekTime()
        const dayKeysSorted = Object.keys(this.segmentsByDate).sort(
          (a, b) => moment(a, 'YYYY_MM_D').valueOf() - moment(b, 'YYYY_MM_D').valueOf()
        )
        this.$emit('timeline-ready', dayKeysSorted, time)
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
        this.segments = []
        this.mergedSegments = []
        this.scheduleDraw()
      } finally {
        this.loading = false
        this.$emit('loading', false)
      }
    },
    applyUnitNormalization(byDate) {
      const sSamples = []
      const dSamples = []
      outer: for (const dayKey of Object.keys(byDate)) {
        const arr = byDate[dayKey]
        for (let i = 0; i < arr.length; i++) {
          sSamples.push(arr[i].s)
          dSamples.push(arr[i].d)
          if (sSamples.length >= 500) break outer
        }
      }
      if (!sSamples.length) return
      const median = (vals) => {
        const a = vals.slice().sort((x, y) => x - y)
        const mid = Math.floor(a.length / 2)
        return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2
      }
      const sMed = median(sSamples)
      const dMed = median(dSamples)
      const looksSeconds = sMed < 1e12 && dMed <= 30
      if (!looksSeconds) return
      for (const key of Object.keys(byDate)) {
        byDate[key] = byDate[key].map((s) => ({ s: s.s * 1000, d: s.d * 1000 }))
      }
    },
    buildDayIndex() {
      const index = {}
      for (const [key, arr] of Object.entries(this.segmentsByDate)) {
        const starts = new Array(arr.length)
        const ends = new Array(arr.length)
        const prefix = new Array(arr.length + 1)
        prefix[0] = 0
        for (let i = 0; i < arr.length; i++) {
          starts[i] = arr[i].s
          ends[i] = arr[i].s + arr[i].d
          prefix[i + 1] = prefix[i] + arr[i].d
        }
        index[key] = {
          starts,
          ends,
          prefix,
          totalMs: prefix[prefix.length - 1]
        }
      }
      this.dayIndex = index
    },
    async refreshSegments() {
      const now = Date.now()
      if (now - this.lastRefreshAt < this.REFRESH_COOLDOWN_MS) return
      this.lastRefreshAt = now
      try {
        const url =
          window.location.protocol === 'http:'
            ? `http://${this.store.currentDivision.address}`
            : `https://${this.store.currentDivision.url}`
        const request = await fetch(`${url}/api/segmentData/${this.camId}`)
        if (!request.ok) return
        const data = await request.json()
        const normalizedByDate = {}
        for (const [key, arr] of Object.entries(data || {})) {
          const normalizedKey = moment(key, ['YYYY_MM_D', 'YYYY_M_D']).format('YYYY_MM_D')
          const normalizedArr = (arr || [])
            .map((s) => ({ s: Number(s.s), d: Number(s.d) }))
            .filter((s) => Number.isFinite(s.s) && Number.isFinite(s.d) && s.d > 0)
            .sort((a, b) => a.s - b.s)
          if (normalizedArr.length) normalizedByDate[normalizedKey] = normalizedArr
        }
        this.applyUnitNormalization(normalizedByDate)
        this.segmentsByDate = normalizedByDate
        let segments = []
        for (let value of Object.values(this.segmentsByDate)) {
          value = value.sort((a, b) => a.s - b.s)
          segments = segments.concat(value)
        }
        this.segments = segments.sort((a, b) => a.s - b.s)
        this.mergeSegments()
        if (typeof this.buildDayIndex === 'function') {
          this.buildDayIndex()
        } else {
          const index = {}
          for (const [key, arr] of Object.entries(this.segmentsByDate)) {
            const starts = new Array(arr.length)
            const ends = new Array(arr.length)
            const prefix = new Array(arr.length + 1)
            prefix[0] = 0
            for (let i = 0; i < arr.length; i++) {
              starts[i] = arr[i].s
              ends[i] = arr[i].s + arr[i].d
              prefix[i + 1] = prefix[i] + arr[i].d
            }
            index[key] = { starts, ends, prefix, totalMs: prefix[prefix.length - 1] }
          }
          this.dayIndex = index
        }
        this.scheduleDraw()
      } catch (_) {
        // silently ignore refresh errors
      }
    },
    async refreshIfNearEnd(centerTime) {
      if (!this.segments.length) return
      const last = this.segments[this.segments.length - 1]
      const lastEnd = last.s + last.d
      if (lastEnd - centerTime <= this.NEAR_END_THRESHOLD_MS) {
        await this.refreshSegments()
      }
    },
    drawCanvas() {
      if (!this.mergedSegments || !this.mergedSegments.length) return
      const canvas = this.$refs.timelineCanvas
      if (!canvas) return;
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw background
      if (!this.store.showGaps) {
        ctx.fillStyle = 'rgb(106, 109, 114)'
      } else {
        ctx.fillStyle = 'rgb(255, 255, 255)'
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height * 0.6)
      // Draw visible merged segments
      ctx.fillStyle = 'rgb(106, 109, 114)'
      let batchStart = null
      let batchEnd = null
      const finishBatch = () => {
        if (batchStart !== null && batchEnd !== null) {
          const startX = Math.floor(this.getXPositionForTime(batchStart))
          const endX = Math.ceil(this.getXPositionForTime(batchEnd))
          const clampedStart = Math.max(0, Math.min(canvas.width - 1, startX))
          const clampedEnd = Math.max(0, Math.min(canvas.width, endX))
          const width = Math.max(1, clampedEnd - clampedStart)
          ctx.fillRect(clampedStart, 0, width, canvas.height * 0.6)
        }
      }
      this.mergedSegments.forEach((segment) => {
        const segmentStart = segment.s
        const segmentEnd = segment.s + segment.d

        // Check if the segment is within the visible time range
        const eps = 1
        if (segmentEnd >= this.visibleStartTime - eps && segmentStart <= this.visibleEndTime + eps) {
          if (batchStart === null) {
            batchStart = segmentStart
            batchEnd = segmentEnd
          } else if (segmentStart - batchEnd < 1000) {
            // 1 second threshold for batching
            batchEnd = segmentEnd
          } else {
            finishBatch()
            batchStart = segmentStart
            batchEnd = segmentEnd
          }
        }
      })

      finishBatch() // Draw the last batch
      this.drawHourlyStripes(ctx, canvas.height)
    },
    drawHourlyStripes(ctx, height) {
      // Calculate time interval based on zoom level
      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      let interval
      let formatString
      if (visibleDuration <= 300000) { // 5 minutes or less
        interval = 10000 // 10 seconds
        formatString = 'HH:mm:ss'
      } else if (visibleDuration <= 1800000) { // 30 minutes or less
        interval = 60000 // 1 minute
        formatString = 'HH:mm'
      } else if (visibleDuration <= 7200000) { // 2 hours or less
        interval = 300000 // 5 minutes
        formatString = 'HH:mm'
      } else if (visibleDuration <= 86400000) { // 24 hours or less
        interval = 3600000 // 1 hour
        formatString = 'HH:mm'
      } else if (visibleDuration <= 300000000) { // 100 hours or less
        interval = 21600000 // 6 hours
        formatString = 'HH:mm'
      } else {
        interval = 43200000 // 12 hours
        formatString = 'HH:mm'
      }

      const startTime = Math.floor(this.visibleStartTime / interval) * interval
      const endTime = Math.ceil(this.visibleEndTime / interval) * interval
      const numberOfMarkers = (endTime - startTime) / interval
      const availableWidthPerLabel = this.canvasWidth / numberOfMarkers
      const baseFontSize = availableWidthPerLabel / 6
      const fontsize = Math.max(6, Math.min(baseFontSize, 15)) * Math.min(2, this.dpi)

      ctx.font = `${fontsize}px Arial`
      ctx.textAlign = 'center'

      for (let time = startTime; time <= endTime; time += interval) {
        const x = this.getXPositionForTime(time)

        // Draw stripe
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)'
        ctx.fillRect(x, 0, 1, height * 0.6)

        // Draw time label
        const timeString = moment(time).utcOffset(this.SERVER_TZ_OFFSET_MINUTES).format(formatString)
        ctx.fillStyle = 'rgba(255, 255, 255, 1)'
        ctx.fillText(timeString, x, height)
      }
    },
    scheduleDraw() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
      }
      this.animationFrameId = requestAnimationFrame(() => {
        this.drawCanvas()
        this.animationFrameId = null
      })
    },
    getXPositionForTime(time) {
      const timeRange = this.visibleEndTime - this.visibleStartTime
      const a = time - this.visibleStartTime
      const b = timeRange > 0 ? a / timeRange : 0
      if (!Number.isFinite(b)) return 0
      const x = b * this.canvasWidth
      return Math.max(0, Math.min(this.canvasWidth, x))
    },
    parseDate(dateString) {
      // Extract year, month, day, hour, minute, second
      const year = parseInt(dateString.slice(0, 4), 10)
      const month = parseInt(dateString.slice(4, 6), 10) - 1 // Month is zero-indexed in JavaScript Date
      const day = parseInt(dateString.slice(6, 8), 10)
      const hour = parseInt(dateString.slice(8, 10), 10)
      const minute = parseInt(dateString.slice(10, 12), 10)
      const second = parseInt(dateString.slice(12, 14), 10)

      // Return the Date object
      return new Date(year, month, day, hour, minute, second)
    },
    mergeSegments() {
      if (this.segments.length === 0) {
        this.mergedSegments = []
        return
      }

      const merged = []
      let current = { ...this.segments[0] }
      let currentYear = moment(current.s).year()

      for (let i = 1; i < this.segments.length; i++) {
        const segment = this.segments[i]
        const segmentYear = moment(segment.s).year()
        const sameDay = moment(segment.s).isSame(moment(current.s), 'day')

        // Check if the segment is within the same year and within the merge threshold
        if (
          segmentYear === currentYear &&
          sameDay &&
          segment.s - (current.s + current.d) <= this.mergeThreshold
        ) {
          // Merge segments by updating the duration
          current.d = segment.s + segment.d - current.s
        } else {
          // Add the current segment to merged and start a new one
          merged.push(current)
          current = { ...segment }
          currentYear = segmentYear
        }
      }

      // Add the last segment
      merged.push(current)

      this.mergedSegments = merged
      this.calculateMinScale()
      this.calculateMaxScale()
    },
    calculateMinScale() {
      if (!this.segments.length) return 1;
      
      // Calculate total timeline duration
      const totalDuration = this.totalTime;
      
      // Calculate the scale needed to show exactly 24 hours
      // Scale = total duration / visible duration
      this.minScale = totalDuration / this.TWENTY_FOUR_HOURS;
      
      // If total duration is less than 24 hours, set minScale to 1
      if (this.minScale < 1) {
        this.minScale = 1;
      }
    },
    calculateMaxScale() {
      if (!this.segments.length) return 500;
      
      // Calculate total timeline duration
      const totalDuration = this.totalTime;
      
      // Calculate the scale needed to show exactly 1 minute
      // Scale = total duration / minimum visible duration
      this.maxScale = totalDuration / this.MINIMUM_VISIBLE_DURATION;
      
      // Set a reasonable minimum and maximum for maxScale
      this.maxScale = Math.max(100, Math.min(this.maxScale, 2000));
    },
    increaseCurrentTime(milliseconds) {
      if (this.mouseDown) return
      if (!this.segments.length) {
        console.warn('No segments available to increase time.')
        return
      }

      // Calculate the current center time of the timeline
      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      let newCenterTime = this.visibleStartTime + visibleDuration / 2 + milliseconds

      // Define the minimum and maximum allowable times
      const minTime = this.segments[0].s
      const maxTime =
        this.segments[this.segments.length - 1].s + this.segments[this.segments.length - 1].d

      // Clamp the new center time within the timeline bounds
      newCenterTime = Math.max(minTime, Math.min(newCenterTime, maxTime))

      // Create a new Date object for the updated center time
      const newDateTime = new Date(newCenterTime)

      // Update the timeline position without emitting events
      this.setTimelinePosition(newDateTime, false)
    },
    getCurrentDayStart(date) {
      const day = this.segmentsByDate[moment(date).utcOffset(this.SERVER_TZ_OFFSET_MINUTES, true).format('YYYY_MM_D')]
      return day && day.length ? day[0].s : null
    },
    setTimelineFromPlayer(date) {
      if (this.mouseDown) return
      this.setTimelinePosition(date, false)
    },
    setLastDayVisible(emitEvent = true) {
      const dayKeys = Object.keys(this.segmentsByDate)
      if (!dayKeys.length) return
      const sorted = dayKeys.sort((a, b) => moment(a, 'YYYY_MM_D').valueOf() - moment(b, 'YYYY_MM_D').valueOf())
      const lastKey = sorted[sorted.length - 1]
      const lastDaySegments = this.segmentsByDate[lastKey]
      if (!lastDaySegments || !lastDaySegments.length) return

      const firstSegmentOfDay = lastDaySegments[0]
      const lastSegmentOfDay = lastDaySegments[lastDaySegments.length - 1]

      const dayStart = firstSegmentOfDay.s
      const dayEnd = lastSegmentOfDay.s + lastSegmentOfDay.d

      this.scale = this.totalTime / (dayEnd - dayStart)

      const time = new Date(dayStart)

      this.setTimelinePosition(time, emitEvent)
    },

    groupSegmentsByDay() {
      const days = []
      let currentDay = null

      for (const segment of this.segments) {
        const segmentDay = moment(segment.s).startOf('day').valueOf()

        if (currentDay === null || segmentDay !== currentDay.date) {
          if (currentDay) {
            currentDay.end =
              currentDay.segments[currentDay.segments.length - 1].s +
              currentDay.segments[currentDay.segments.length - 1].d
            days.push(currentDay)
          }
          currentDay = {
            date: segmentDay,
            start: segment.s,
            segments: []
          }
        }

        currentDay.segments.push(segment)
      }

      if (currentDay) {
        currentDay.end =
          currentDay.segments[currentDay.segments.length - 1].s +
          currentDay.segments[currentDay.segments.length - 1].d
        days.push(currentDay)
      }

      return days
    },
    onMouseDown(e) {
      this.mouseDown = true
      this.dragStartX = e.clientX
      this.$emit('seek-start')
      this.seekEvents = 0;
      document.addEventListener('mousemove', this.onMouseDrag)
      document.addEventListener('mouseup', this.onMouseUp)
    },

    onMouseDrag(e) {
      if (!this.mouseDown || this.loading) return
      const dx = e.clientX - this.dragStartX

      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      const timeDiff = (dx / this.canvasWidth) * visibleDuration

      // Calculate new offsetX
      const newOffsetX = this.offsetX - timeDiff / this.totalTime
      const minOffsetX = -0.5 / this.scale
      const maxOffsetX = 1 - 0.5 / this.scale
      this.offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, newOffsetX))
      this.dragStartX = e.clientX
      this.updateCurrentSegmentName()
      this.scheduleDraw()
      const seekTime = this.calculateSeekTime()
      this.$emit('seek', seekTime)
      this.seekEvents++
    },

    onMouseUp() {
      if (this.mouseDown && !this.loading) {
        this.updateCurrentSegmentName()
        this.scheduleDraw()

        const visibleDuration = this.visibleEndTime - this.visibleStartTime
        const centerTime = this.visibleStartTime + visibleDuration / 2

        // Find the first segment that starts after the current scrubber position
        const nextSegment = this.segments.find((segment) => segment.s > centerTime)

        if (nextSegment) {
          // Move the timeline to the start of the next segment
          this.setTimelinePosition(new Date(nextSegment.s), false)
        } else {
          // If no segment is found to the right, stay at the current position
          this.setTimelinePosition(new Date(centerTime), false)
        }
        this.$emit('seek-end')
        // Try to refresh near the end to catch new recordings
        this.refreshIfNearEnd(centerTime)
      }

      this.mouseDown = false
      document.removeEventListener('mousemove', this.onMouseDrag)
      document.removeEventListener('mouseup', this.onMouseUp)
    },

    calculateSeekTime() {
      if (!this.segments || !this.segments.length) {
        return { d: moment().utcOffset(this.SERVER_TZ_OFFSET_MINUTES).format('YYYY_MM_D'), time: 0 }
      }

      // Center time is the source of truth
      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      const centerTime = this.visibleStartTime + visibleDuration / 2

      // Day selection by nearest segment
      const closest = this.findClosestSegment(centerTime)
      const dayKey = moment(closest.s).utcOffset(this.SERVER_TZ_OFFSET_MINUTES).format('YYYY_MM_D')
      const arr = this.segmentsByDate[dayKey] || []
      const idx = this.dayIndex[dayKey]
      if (!arr.length || !idx) return { d: dayKey, time: 0 }

      // Clamp center time to playable range
      const dayStart = idx.starts[0]
      const dayEnd = idx.ends[idx.ends.length - 1]
      const epsilonMs = this.END_EPSILON_SECONDS * 1000
      const clamped = Math.min(Math.max(centerTime, dayStart), Math.max(dayStart, dayEnd - epsilonMs))

      // Binary search: first segment with end > clamped
      const ends = idx.ends
      let lo = 0, hi = ends.length
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (ends[mid] <= clamped) lo = mid + 1
        else hi = mid
      }
      const k = lo // number of segments fully before clamped
      const prefix = idx.prefix
      let ms = prefix[k]
      if (k < arr.length) {
        const segStart = idx.starts[k]
        if (clamped > segStart) {
          ms += clamped - segStart
        }
      }
      return { d: dayKey, time: ms / 1000 }
    },
    seekToNextDay(date) {
      const nextDay = moment(date).utcOffset(this.SERVER_TZ_OFFSET_MINUTES, true).add(1, 'day').startOf('day')
      const nextDayStr = nextDay.format('YYYY_MM_D')
      const nextDaySegments = this.segmentsByDate[nextDayStr]
      if (!nextDaySegments || !nextDaySegments.length) return false
      const firstSeg = nextDaySegments[0]
      this.setTimelinePosition(new Date(firstSeg.s))
      return true
    },
    updateCurrentSegmentName() {
      if (!this.segments.length) return
      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      const centerTime = this.visibleStartTime + visibleDuration / 2
      this.currentSegmentName = moment(centerTime)
        .utcOffset(this.SERVER_TZ_OFFSET_MINUTES)
        .format('YYYY-MM-DD HH:mm:ss')
    },

    setTimelinePosition(dateTime, emitEvent = true) {
      const targetTime = moment(dateTime).valueOf()
      // Find the closest segment to the target time  
      const closestSegment = this.findClosestSegment(targetTime)
      if (!closestSegment) {
        console.warn('No segments available')
        return
      }

      // Calculate the time to center on (either target time or start of closest segment)
      const centerTime =
        targetTime >= closestSegment.s && targetTime < closestSegment.s + closestSegment.d
          ? targetTime
          : closestSegment.s

      // Calculate the offset to center the target time
      const timeFromStart = centerTime - this.segments[0].s
      this.offsetX = timeFromStart / this.totalTime - 0.5 / this.scale
      const minOffsetX = -0.5 / this.scale
      const maxOffsetX = 1 - 0.5 / this.scale
      this.offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, this.offsetX))

      // Update the view
      this.updateCurrentSegmentName()
      this.scheduleDraw()
      // If we are navigating near the end, refresh segments in background
      this.refreshIfNearEnd(centerTime)

      if (emitEvent) {
        // Emit seek event
        const seekTime = this.calculateSeekTime()
        this.$emit('seek', seekTime);
        console.log('seekTime', seekTime);
        // If day changed, store pending reseek to emit after playlist is loaded
        if (!this.lastSeekDayKey || this.lastSeekDayKey !== seekTime.d) {
          this.lastSeekDayKey = seekTime.d
          this.pendingReseekPayload = { ...seekTime }
        } else {
          this.pendingReseekPayload = null
        }
      }
    },
    // Called by parent (player) once playlist is loaded and ready to seek
    onPlaylistLoaded() {
      if (this.pendingReseekPayload) {
        this.$emit('seek', this.pendingReseekPayload)
        this.pendingReseekPayload = null
      }
    },
    setTimelineToDate(inputDate) {
      // Calculate the current center time of the timeline
      const visibleDuration = this.visibleEndTime - this.visibleStartTime
      const centerTime = this.visibleStartTime + visibleDuration / 2

      // Extract the time components (hours, minutes, seconds, milliseconds) from the current center time
      const centerMoment = moment(centerTime).utcOffset(this.SERVER_TZ_OFFSET_MINUTES)
      const timeComponents = {
        hour: centerMoment.hour(),
        minute: centerMoment.minute(),
        second: centerMoment.second(),
        millisecond: centerMoment.millisecond()
      }

      // Combine the input date with the existing time components to create a new DateTime
      const newDateTime = moment(inputDate).utcOffset(this.SERVER_TZ_OFFSET_MINUTES, true).set(timeComponents).toDate()

      // Update the timeline position to the new DateTime
      this.setTimelinePosition(newDateTime)
    },
    findClosestSegment(time) {
      return this.segments.reduce((closest, segment) => {
        const currentDiff = Math.abs(time - (segment.s + segment.d / 2))
        const closestDiff = Math.abs(time - (closest.s + closest.d / 2))
        return currentDiff < closestDiff ? segment : closest
      })
    },

    onProgress(time) {
      if (!this.segments.length || this.mouseDown) return
      const startTime = this.segments[0].s
      let targetTime = startTime + time
      const containing = this.segments.find((s) => targetTime >= s.s && targetTime < s.s + s.d)
      if (!containing) {
        const next = this.segments.find((s) => s.s > targetTime)
        targetTime = next ? next.s : this.segments[0].s
      }
      // Center timeline on the target time without emitting seek event
      this.setTimelinePosition(new Date(targetTime), false)
      this.scheduleDraw()
    },
    onWheel(e) {
      e.preventDefault()
      const scaleFactor = e.deltaY > 0 ? 1.1 : 0.9
      this.updateScale(scaleFactor)
    },

    onTouchStart(e) {
      this.allowMouseEvents = false
      if (e.touches.length === 1) {
        this.touchDrag = true
        this.dragStartX = e.touches[0].clientX
        window.addEventListener('touchend', this.onTouchEnd)
        window.addEventListener('touchmove', this.onTouchMove)
        this.$emit('seek-start')
      }
      if (e.touches.length === 2) {
        this.touchStartDistance = this.getTouchDistance(e.touches)
      }
    },

    onTouchMove(e) {
      if (e.touches.length === 1 && !this.loading && this.touchDrag) {
        const event = e.touches[0]
        const dx = event.clientX - this.dragStartX
        const visibleDuration = this.visibleEndTime - this.visibleStartTime
        const timeDiff = (dx / this.canvasWidth) * visibleDuration

        // Calculate new offsetX
        const newOffsetX = this.offsetX - timeDiff / this.totalTime
        const minOffsetX = -0.5 / this.scale
        const maxOffsetX = 1 - 0.5 / this.scale
        this.offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, newOffsetX))
        this.dragStartX = event.clientX
        this.updateCurrentSegmentName()
        this.scheduleDraw()
        const seekTime = this.calculateSeekTime()
        this.$emit('seek', seekTime)
      }
      if (e.touches.length === 2) {
        const currentDistance = this.getTouchDistance(e.touches)
        this.touchStartDistance = currentDistance
      }
    },

    onTouchEnd() {
      window.removeEventListener('touchend', this.onTouchEnd)
      window.removeEventListener('touchmove', this.onTouchMove)
      this.touchDrag = false
      // Calculate and emit the seek time
      this.touchStartDistance = 0
      this.$emit('seek-end')
    },

    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },

    updateScale(scaleFactor) {
      const oldScale = this.scale
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, oldScale * scaleFactor))
      const startTime = this.segments[0].s
      const currentTime =
        startTime + this.offsetX * this.totalTime + this.totalTime / this.scale / 2
      this.scale = newScale
      this.setTimelinePosition(new Date(currentTime), false)
      this.scheduleDraw()
    },

    formatTime(timestamp) {
      return moment(timestamp).format('HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.timeline-container {
  position: relative;
  width: 100%;
}
.canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.timeline-canvas {
  touch-action: none;
  background-size: 200% 100%;
}
.scrubber {
  position: absolute;
  top: -3px;
  left: 50%;
  width: 2px;
  height: 85%;
  background-color: #fff;
  pointer-events: none;
}

.scrubber::after,
.scrubber::before {
  content: '';
  position: absolute;
  left: -2px;
  width: 0;
  height: 0;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
}

.scrubber::before {
  top: 0;
  border-top: 5px solid #fff;
}

.scrubber::after {
  bottom: 0;
  border-bottom: 5px solid #fff;
}

.tt {
  position: absolute;
  top: -25px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
}

.params {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cont {
  width: 100%;
}

.error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 18px;
  white-space: nowrap;
}

.time-label {
  position: absolute;
  bottom: -20px;
  font-size: 12px;
  color: #fff;
}

.start-time {
  left: 0;
}

.end-time {
  right: 0;
}
</style>
